import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Backdrop from '@material-ui/core/Backdrop';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import { PaperProps } from '@material-ui/core/Paper';
import _ from 'underscore';
import uniqid from 'uniqid';
import { useDebounce } from 'use-debounce';

import { add, remove } from '../../redux/dialogs/dialogsSlice';
import { ReduxState } from '../../redux/types';
import { BackdropNoTransition, StyledPaper } from './styled';

/*
 * This component wrap the Dialog component, provided by Material-UI,
 * and do some magic in order to display just the dimmed backdrop
 * of the last dialog shown (the foreground one), to prevent the page
 * to become more dark every time a new dialog opens,
 * where one or more dialogs are already open.
 *
 * Also, to avoid overlapping too many dialogs, only the last one is visible,
 * except in the case where the alwaysVisible prop is true.
 *
 * It basically keep the list of the visible dialog ids in the Redux state,
 * in order to the determinate if the current one is the last opened.
 */

interface Props extends DialogProps {
    alwaysVisible?: boolean;
}

const CustomDialog = (props: Props) => {
    const { open, alwaysVisible, ...dialogProps } = props;

    const dialogId = useRef<string | null>(null);

    const [firstDialog] = useSelector((state: ReduxState) => state.dialogs.slice(0, 1), _.isEqual);
    const [foregroundDialog] = useSelector((state: ReduxState) => state.dialogs.slice(-1), _.isEqual);

    const [debouncedFirstDialog] = useDebounce(firstDialog, 250);

    const dispatch = useDispatch();

    useEffect(() => {
        // when the component is mounted, an unique id is generated for it
        dialogId.current = uniqid();
    }, []);

    useEffect(() => {
        // when the dialog is opened/closed, its id is addedd/removed from the array in the app state
        if (!dialogId) {
            // if dialogId is null do nothing, it may not have been generated yet
            return;
        }

        const action = open ? add : remove;
        dispatch(action(dialogId.current));
    }, [dispatch, open, dialogId]);

    // if the current dialog is not the first one, we use the backdrop without transition
    const BackdropComponent = [firstDialog, debouncedFirstDialog].some(id => id === dialogId.current) ? Backdrop : BackdropNoTransition;

    return (
        <Dialog
            open={open}
            BackdropComponent={BackdropComponent}
            BackdropProps={{invisible: !(foregroundDialog === dialogId.current || !foregroundDialog)}}
            PaperComponent={StyledPaper}
            PaperProps={{ show: ((foregroundDialog === dialogId.current) || alwaysVisible) ? 1 : 0 } as PaperProps}
            {...dialogProps}
        />
    );
};

export default CustomDialog;
